import {
  NotDisclosed,
  RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum,
} from "@platform/openapi-types";

import { shouldIncludeBenefitInApi } from "./typeAmountTransforms";

import type {
  BenefitDescriptionApi,
  BenefitMaximumApi,
  BenefitPersonApi,
  BenefitWithOptionalType,
  GuaranteeIssueApi,
} from "./types";

type GridMultiOption = { value: number };

function isValidPositiveNumber(value: unknown): value is number {
  return (
    value !== undefined &&
    value !== null &&
    typeof value === "number" &&
    value >= 0
  );
}

function mapBenefitDescriptionTypeToApi(frontendType: string): string {
  if (frontendType === "multipleDollarAmounts") {
    return "multipleOptions";
  }
  if (frontendType === "multipleSalaryMultipliers") {
    return "salaryMultiplier";
  }
  return frontendType;
}

function extractAmountsFromArrayType(
  benefitDescriptionAmount: unknown,
): number[] {
  if (!Array.isArray(benefitDescriptionAmount)) {
    return [];
  }

  return benefitDescriptionAmount
    .filter(
      (option: unknown) =>
        option &&
        typeof option === "object" &&
        "amount" in option &&
        typeof option.amount === "number" &&
        option.amount > 0,
    )
    .map((option: { amount: number }) => option.amount);
}

const buildBenefitDescriptionReturn = (
  type: string,
  amounts?: number[],
  minimum?: number,
): BenefitDescriptionApi | undefined => {
  const result: BenefitDescriptionApi = { type };
  if (amounts !== undefined) {
    result.amounts = amounts;
  }
  if (minimum !== undefined) {
    result.minimum = minimum;
  }
  return result;
};

function buildBenefitDescription(
  benefitDescriptionType: string,
  benefitDescriptionAmount: unknown,
  benefitDescriptionMinimum: number | undefined,
): BenefitDescriptionApi | undefined {
  if (!shouldIncludeBenefitInApi(benefitDescriptionType)) {
    return undefined;
  }

  const apiType = mapBenefitDescriptionTypeToApi(benefitDescriptionType);

  if (
    benefitDescriptionType === "multipleDollarAmounts" ||
    benefitDescriptionType === "multipleSalaryMultipliers"
  ) {
    const amounts = extractAmountsFromArrayType(benefitDescriptionAmount);
    if (amounts.length > 0) {
      return buildBenefitDescriptionReturn(apiType, amounts);
    }
  } else if (
    benefitDescriptionType === "dollarAmount" ||
    benefitDescriptionType === "increments" ||
    benefitDescriptionType === "salaryMultiplier" ||
    benefitDescriptionType === "percentage"
  ) {
    let amounts: number[] | undefined = undefined;
    if (isValidPositiveNumber(benefitDescriptionAmount)) {
      amounts = [benefitDescriptionAmount];
    }
    let minimum: number | undefined = undefined;
    if (isValidPositiveNumber(benefitDescriptionMinimum)) {
      minimum = benefitDescriptionMinimum;
    }
    if (amounts !== undefined || minimum !== undefined) {
      return buildBenefitDescriptionReturn(apiType, amounts, minimum);
    }
  } else {
    return buildBenefitDescriptionReturn(apiType);
  }

  return undefined;
}

function buildBenefitMaximum(
  benefitMaximumType: string | undefined,
  benefitMaximumDollarAmount: number | undefined,
  benefitMaximumPercent: number | undefined,
): BenefitMaximumApi | undefined {
  if (!shouldIncludeBenefitInApi(benefitMaximumType)) {
    return undefined;
  }

  const result: BenefitMaximumApi = { type: benefitMaximumType };

  const includesDollarAmount =
    benefitMaximumType === "dollarAmount" ||
    benefitMaximumType === "percentageOfEeBenefitAndDollarAmount";
  const includesPercent =
    benefitMaximumType === "percentageOfEeBenefit" ||
    benefitMaximumType === "percentageOfEeBenefitAndDollarAmount";

  if (
    includesDollarAmount &&
    isValidPositiveNumber(benefitMaximumDollarAmount)
  ) {
    result.dollarAmount = benefitMaximumDollarAmount;
  }

  if (includesPercent && isValidPositiveNumber(benefitMaximumPercent)) {
    result.percent = benefitMaximumPercent;
  }

  return result;
}

function buildGuaranteeIssue(
  guaranteeIssueType: string | undefined,
  guaranteeIssueAmount: number | undefined,
): GuaranteeIssueApi | undefined {
  if (!shouldIncludeBenefitInApi(guaranteeIssueType)) {
    return undefined;
  }

  const result: GuaranteeIssueApi = { type: guaranteeIssueType };

  if (
    (guaranteeIssueType ===
      RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum.percentageOfEeBenefit ||
      guaranteeIssueType ===
        RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum.flatAmount) &&
    isValidPositiveNumber(guaranteeIssueAmount)
  ) {
    result.amount = guaranteeIssueAmount;
  }

  return result;
}

/**
 * Removes all fields except 'type' from NotDisclosed benefits.
 */
export function cleanupNotDisclosedBenefits<T>(apiObject: T): T {
  if (!apiObject || typeof apiObject !== "object") {
    return apiObject;
  }

  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(apiObject)) {
    const isBenefitObject = (val: unknown): val is BenefitWithOptionalType =>
      val !== null &&
      typeof val === "object" &&
      !Array.isArray(val) &&
      "type" in val;

    if (isBenefitObject(value) && value.type === NotDisclosed) {
      cleaned[key] = { type: NotDisclosed };
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned as T;
}

/**
 * Transforms employee/spouse/child benefit fields to API format.
 */
export function transformBenefitPersonToApi(
  benefitDescriptionType: string,
  benefitDescriptionAmount: unknown,
  benefitDescriptionMinimum: number | undefined,
  benefitMaximumType: string | undefined,
  benefitMaximumDollarAmount: number | undefined,
  benefitMaximumPercent: number | undefined,
  guaranteeIssueType: string | undefined,
  guaranteeIssueAmount: number | undefined,
): BenefitPersonApi {
  const result: BenefitPersonApi = {};

  const benefitDescription = buildBenefitDescription(
    benefitDescriptionType,
    benefitDescriptionAmount,
    benefitDescriptionMinimum,
  );
  if (benefitDescription) {
    result.benefitDescription = benefitDescription;
  }

  const benefitMaximum = buildBenefitMaximum(
    benefitMaximumType,
    benefitMaximumDollarAmount,
    benefitMaximumPercent,
  );
  if (benefitMaximum) {
    result.benefitMaximum = benefitMaximum;
  }

  const guaranteeIssue = buildGuaranteeIssue(
    guaranteeIssueType,
    guaranteeIssueAmount,
  );
  if (guaranteeIssue) {
    result.guaranteeIssue = guaranteeIssue;
  }

  return result;
}

function parseBenefitDescriptionFromApi(
  desc: BenefitDescriptionApi,
  prefix: string,
): Record<string, unknown> {
  const apiType = desc.type || "dollarAmount";

  let frontendType = apiType;
  if (apiType === "multipleOptions") {
    frontendType = "multipleDollarAmounts";
  } else if (
    apiType === "salaryMultiplier" &&
    Array.isArray(desc.amounts) &&
    desc.amounts.length > 1
  ) {
    frontendType = "multipleSalaryMultipliers";
  }

  const isMulti =
    frontendType === "multipleDollarAmounts" ||
    frontendType === "multipleSalaryMultipliers";

  return {
    [`${prefix}BenefitDescriptionType`]: frontendType,
    [`${prefix}BenefitDescriptionAmount`]: isMulti
      ? (desc.amounts ?? [])
          .filter((v): v is number => typeof v === "number" && v > 0)
          .map((v) => ({ amount: v }))
      : desc.amounts?.[0],
    [`${prefix}BenefitDescriptionMinimum`]: isMulti ? undefined : desc.minimum,
  };
}

function parseBenefitMaximumFromApi(
  max: BenefitMaximumApi | undefined,
  prefix: string,
): Record<string, unknown> {
  if (!max) {
    return {
      [`${prefix}BenefitMaximumType`]: undefined,
      [`${prefix}BenefitMaximumDollarAmount`]: undefined,
      [`${prefix}BenefitMaximumPercent`]: undefined,
    };
  }

  const type = max.type || "dollarAmount";
  const dollarAmountValid =
    typeof max.dollarAmount === "number" && max.dollarAmount >= 0;
  const percentValid = typeof max.percent === "number" && max.percent >= 0;

  const shouldBlank =
    (type === "dollarAmount" && !dollarAmountValid) ||
    (type === "percentageOfEeBenefitAndDollarAmount" &&
      (!dollarAmountValid || !percentValid));

  if (shouldBlank) {
    return {
      [`${prefix}BenefitMaximumType`]: undefined,
      [`${prefix}BenefitMaximumDollarAmount`]: undefined,
      [`${prefix}BenefitMaximumPercent`]: undefined,
    };
  }

  return {
    [`${prefix}BenefitMaximumType`]: type,
    [`${prefix}BenefitMaximumDollarAmount`]: max.dollarAmount,
    [`${prefix}BenefitMaximumPercent`]: max.percent,
  };
}

export function transformBenefitPersonFromApi(
  apiPerson: BenefitPersonApi | null | undefined,
  prefix: string,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (apiPerson?.benefitDescription) {
    Object.assign(
      result,
      parseBenefitDescriptionFromApi(apiPerson.benefitDescription, prefix),
    );
  }

  Object.assign(
    result,
    parseBenefitMaximumFromApi(apiPerson?.benefitMaximum, prefix),
  );

  // An absent guaranteeIssue means the column was never filled in, so leave the
  // grid cell blank rather than substituting a sentinel — the same treatment
  // parseBenefitMaximumFromApi already gives an absent benefitMaximum. Inventing
  // "notIncluded" here rendered it over NULL columns and then persisted it on the
  // next save.
  if (apiPerson?.guaranteeIssue) {
    const gi = apiPerson.guaranteeIssue;
    result[`${prefix}GuaranteeIssueType`] = gi.type;
    result[`${prefix}GuaranteeIssueAmount`] = gi.amount;
  }

  return result;
}

function isGridMultiOption(option: unknown): option is GridMultiOption {
  return (
    option !== null &&
    typeof option === "object" &&
    "value" in option &&
    typeof (option as GridMultiOption).value === "number" &&
    (option as GridMultiOption).value > 0
  );
}

function extractAmountsFromMultiOptions(multiOptions: unknown[]): number[] {
  return multiOptions.filter(isGridMultiOption).map((option) => option.value);
}

function addRegularAmountsToResult(
  type: string,
  amountFields: Record<string, unknown>,
  result: Record<string, unknown>,
): void {
  const shouldInclude =
    type !== "notIncluded" && type !== "notOffered" && type !== "notApplicable";
  if (!shouldInclude) return;

  for (const [fieldName, fieldValue] of Object.entries(amountFields)) {
    if (fieldName === "multiOptions") continue;
    if (typeof fieldValue === "number" && fieldValue >= 0) {
      result[fieldName] = fieldValue;
    }
  }
}

/**
 * Transforms benefit description with multiple options from grid format to API format.
 */
export function transformBenefitDescriptionToApi(
  typeValue: string | undefined,
  amountFields: Record<string, unknown>,
): Record<string, unknown> {
  const type = typeValue ?? "notIncluded";
  const result: Record<string, unknown> = { type };

  if (type === "multipleOptions" && amountFields["multiOptions"]) {
    const multiOptions = amountFields["multiOptions"];
    if (Array.isArray(multiOptions) && multiOptions.length > 0) {
      const amounts = extractAmountsFromMultiOptions(multiOptions);
      if (amounts.length > 0) {
        result["amounts"] = amounts;
      }
    }
  } else {
    addRegularAmountsToResult(type, amountFields, result);
  }

  return result;
}

function toGridFieldName(prefix: string, fieldName: string): string {
  return `${prefix}${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
}

// Heuristic: values < 50 are salary multipliers, >= 50 are dollar amounts
function toTypedOption(value: number): { type: string; value: number } {
  return { type: value < 50 ? "salaryMultiplier" : "dollarAmount", value };
}

function buildMultipleOptionsFields(
  result: Record<string, unknown>,
  apiAmounts: unknown,
  amountKey: string,
): void {
  if (Array.isArray(apiAmounts) && apiAmounts.length > 0) {
    result[amountKey] = apiAmounts
      .filter(
        (value): value is number => typeof value === "number" && value > 0,
      )
      .map(toTypedOption);
  } else {
    result[amountKey] = [];
  }
}

/**
 * Transforms benefit description with multiple options from API format to grid format.
 *
 * For "multipleOptions" type, converts a flat amounts array to typed options.
 * Uses a heuristic: values < 50 are salary multipliers, >= 50 are dollar amounts.
 */
export function transformBenefitDescriptionFromApi(
  apiObject: Record<string, unknown> | null | undefined,
  prefix: string,
  amountFieldNames: string[],
  defaultType: string = "notIncluded",
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const typeKey = `${prefix}Type`;
  const amountKey = `${prefix}Amount`;

  if (apiObject && typeof apiObject === "object") {
    const apiType = (apiObject["type"] as string | undefined) || defaultType;
    result[typeKey] = apiType;

    if (apiType === "multipleOptions" && apiObject["amounts"]) {
      buildMultipleOptionsFields(result, apiObject["amounts"], amountKey);
      for (const fieldName of amountFieldNames) {
        if (fieldName === "amount") continue;
        result[toGridFieldName(prefix, fieldName)] = undefined;
      }
    } else {
      for (const fieldName of amountFieldNames) {
        result[toGridFieldName(prefix, fieldName)] = apiObject[fieldName];
      }
    }
  } else {
    result[typeKey] = defaultType;
    for (const fieldName of amountFieldNames) {
      result[toGridFieldName(prefix, fieldName)] = undefined;
    }
  }

  return result;
}
